import { useEffect, useRef, useState } from "react";
import React from "react";
import { Form, Modal, Row, Skeleton } from "antd";
import { WfFormRenderer } from "./FormRenderer/WfFormRenderer";
import styles from "./Form27TableRowEditorDialog.module.css";
import { Button } from "primereact/button";
import { flatAdvanceForm } from "../../../helpers/advance-form.helper";
import { getTemplateById } from "../services/form.service";
import {
  getAccountCodeProduct,
  getMasterCatalogGroup,
  getPlan,
  getSourceOfFunds,
} from "../services/master-data.service";
import { set, useForm, UseFormReturn } from "react-hook-form";
import { useForm27RequestScreenContext } from "../hooks/RequestScreenContext.hook";

export function Form27TableRowEditorDialog({
  visible,
  setVisible,
  row,
  onSubmit,
}: Readonly<{
  visible: boolean;
  setVisible: (visible: boolean) => void;
  row?: { index: number; data: any; object: any };
  onSubmit: (data: any) => void;
}>) {
  const namespace = Form27TableRowEditorDialog.name;
  enum CONTROL_NAME_ENUM {
    PRODUCT_TYPE = "ประเภทรายการ",
    PLAN_CODE = "รหัสแผนงาน",
    PLAN = "แผนงาน",
    PROJECT = "โครงการ",
    ACTIVITY = "กิจกรรม",
    ACTIVITY_CODE = "รหัสกิจกรรม",
    SoF_CODE = "รหัสแหล่งเงิน",
    SoF = "แหล่งเงิน",
    SoF_SUB_CODE = "รหัสบัญชีย่อย",
    SoF_SUB = "บัญชีย่อย",
    ACCOUNT_CODE = "รหัสบัญชี",
    ACCOUNT_NAME = "ชื่อบัญชี",
    AMOUNT = "จำนวนเงิน",
    VAT_RATE = "อัตราภาษีมูลค่าเพิ่ม",
    VAT_AMOUNT = "อัตราภาษีมูลค่าเพิ่มเป็นเงิน",
    BEFORE_VAT_AMOUNT = "จำนวนเงินก่อน VAT",
  }
  const formRef = useRef<{
    subject: string | null;
    advanceForm: AdvanceForm | null;
    objectForm: any;
    flatForm: any[];
  }>({
    subject: null,
    advanceForm: null,
    objectForm: {},
    flatForm: [],
  });
  const options = useRef<any>({
    catalogGroups: [],
    accountCodeProducs: [],
    plans: [],
    sourceOfFunds: [],
  });
  const [formState, setFormState] = useState<{
    subject: string | null;
    advanceForm: AdvanceForm | null;
    flatForm: any[];
  }>({
    subject: null,
    advanceForm: null,
    flatForm: [],
  });
  const requestForm = useForm27RequestScreenContext();
  const form = useForm();
  useEffect(() => {
    return () => {
      setVisible(false);
    };
  }, []);

  useEffect(() => {
    if (visible && row) {
      setupForm();
    }

    return () => {
      form.reset();
      setFormState({ advanceForm: null, flatForm: [], subject: null });
    };
  }, [visible, row]);

  /**
   * รหัสแผนงาน
   */
  useEffect(() => {
    const code = form.getValues(CONTROL_NAME_ENUM.PLAN_CODE);
    if (code) {
      const item = options.current.plans.find(
        (item: any) => item.Code === code
      );
      form.setValue(CONTROL_NAME_ENUM.PLAN, item?.PlanName);

      form.setValue(CONTROL_NAME_ENUM.PROJECT, item?.ProjectName);
      form.setValue(CONTROL_NAME_ENUM.ACTIVITY, item?.ProjectActivityName);
      form.setValue(CONTROL_NAME_ENUM.SoF_SUB_CODE, "");
      setFormState((prv) => ({
        ...prv,
        advanceForm: formRef.current.advanceForm,
      }));
    } else {
      form.resetField(CONTROL_NAME_ENUM.PROJECT);
      form.resetField(CONTROL_NAME_ENUM.ACTIVITY);
    }
  }, [form.watch(CONTROL_NAME_ENUM.PLAN_CODE)]);

  /**
   * รหัสแหล่งเงิน
   */
  useEffect(() => {
    const code = form.getValues(CONTROL_NAME_ENUM.SoF_CODE);
    if (code) {
      const item = options.current.sourceOfFunds.find(
        (item: any) => item.SoFCode === code
      );
      form.setValue(CONTROL_NAME_ENUM.SoF, item?.SoFName);

      const soFSubAccounts = item?.SoFSubAccount || [];

      formRef.current.advanceForm = setOptions(
        formRef.current.advanceForm!,
        CONTROL_NAME_ENUM.SoF_SUB_CODE,
        soFSubAccounts,
        "SoFSubCode"
      );
      form.setValue(CONTROL_NAME_ENUM.SoF_SUB_CODE, "");
      setFormState((prv) => ({
        ...prv,
        advanceForm: formRef.current.advanceForm,
      }));
    }
  }, [form.watch(CONTROL_NAME_ENUM.SoF_CODE)]);

  /**
   * รหัสบัญชีย่อย
   */
  useEffect(() => {
    const parentCode = form.getValues(CONTROL_NAME_ENUM.SoF_CODE);
    const code = form.getValues(CONTROL_NAME_ENUM.SoF_SUB_CODE);
    if (parentCode && code) {
      const parentItem = options.current.sourceOfFunds.find(
        (item: any) => item.SoFCode === parentCode
      );
      const item = parentItem?.SoFSubAccount.find(
        (item: any) => item.SoFSubCode === code
      );
      form.setValue(CONTROL_NAME_ENUM.SoF_SUB, item?.SoFSubName);
    } else {
      form.setValue(CONTROL_NAME_ENUM.SoF_SUB, null);
    }
  }, [form.watch(CONTROL_NAME_ENUM.SoF_SUB_CODE)]);

  /**
   * รหัสบัญชี
   */
  useEffect(() => {
    const code = form.getValues(CONTROL_NAME_ENUM.ACCOUNT_CODE);
    if (code) {
      const item = options.current.accountCodeProducts.find(
        (item: any) => item.BankAccountCode === code
      );
      form.setValue(CONTROL_NAME_ENUM.ACCOUNT_NAME, item?.BankAccountName);
    }
  }, [form.watch(CONTROL_NAME_ENUM.ACCOUNT_CODE)]);

  useEffect(() => {
    const amount: string = (form.watch(CONTROL_NAME_ENUM.AMOUNT) ?? "")
      .toString()
      .replace(/[^0-9.]/g, "");
    const vatRate: string = (form.watch(CONTROL_NAME_ENUM.VAT_RATE) ?? "")
      .toString()
      .replace(/[^0-9.]/g, "");
    const vatAmount = parseFloat(amount) * (parseFloat(vatRate) / 100);
    const beforeVatAmount = parseFloat(amount) - vatAmount;
    if (!isNaN(vatAmount)) {
      form.setValue(
        CONTROL_NAME_ENUM.VAT_AMOUNT,
        vatAmount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    }
    if (!isNaN(beforeVatAmount)) {
      form.setValue(
        CONTROL_NAME_ENUM.BEFORE_VAT_AMOUNT,
        beforeVatAmount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    }
  }, [
    form.watch(CONTROL_NAME_ENUM.AMOUNT),
    form.watch(CONTROL_NAME_ENUM.VAT_RATE),
  ]);

  async function setupForm() {
    const employeeData = JSON.parse(
      localStorage.getItem("userData") || "{}"
    ).employeeData;

    const [
      _templateById,
      _catalogGroups,
      _accountCodeProducts,
      _plans,
      _sourceOfFunds,
    ] = await Promise.all([
      getTemplateById(54),
      getMasterCatalogGroup(),
      getAccountCodeProduct(),
      getPlan(employeeData?.DepartmentCode),
      getSourceOfFunds(),
    ]);

    options.current = {
      catalogGroups: _catalogGroups,
      accountCodeProducts: _accountCodeProducts,
      plans: _plans,
      sourceOfFunds: _sourceOfFunds,
    };
    if (_templateById == null) {
      return;
    }
    formRef.current.subject = _templateById.subject;
    formRef.current.advanceForm = _templateById.advanceForm;
    formRef.current.advanceForm = setOptions(
      formRef.current.advanceForm,
      CONTROL_NAME_ENUM.PRODUCT_TYPE,
      options.current.catalogGroups,
      "Text",
      "Code"
    );

    formRef.current.advanceForm = setOptions(
      formRef.current.advanceForm,
      CONTROL_NAME_ENUM.PLAN_CODE,
      options.current.plans,
      "Code"
    );

    formRef.current.advanceForm = setOptions(
      formRef.current.advanceForm,
      CONTROL_NAME_ENUM.SoF_CODE,
      options.current.sourceOfFunds,
      "SoFCode"
    );

    formRef.current.advanceForm = setOptions(
      formRef.current.advanceForm,
      CONTROL_NAME_ENUM.ACCOUNT_CODE,
      options.current.accountCodeProducts,
      "BankAccountCode"
    );
    // const form27TableBSession = JSON.parse(
    //   sessionStorage.getItem("form27TableB") || "[]"
    // );
    // let sessionData = form27TableBSession.find(
    //   (_: any, i: number) => i == row?.index
    // );
    // let sessionData = row?.data;

    const flatForm = flatAdvanceForm(formRef.current.advanceForm?.items)
      ?.filter((item) => {
        if (item == null) {
          return false;
        }
        if (item.template == null) {
          return false;
        }
        if (["em", "l"].includes(item.template.type)) {
          return false;
        }
        return true;
      })
      .map((item) => {
        
        const label = item.template.label;
        let defaultValue = item.template.attribute?.default;
        switch (item.template.type) {
          case "r":
            defaultValue = item.template.attribute.items?.find(
              (item: any) => item.checked == "Y"
            )?.item;
            break;
        }
        let value = "";
        switch (label) {
          case "จำนวนเงิน":
            value = row?.data?.จำนวนเงิน;
            break;
          case "ประเภทใบสำคัญ":
            value = requestForm?.form["ประเภทใบสำคัญ"];
            break;
          case "เลขที่ใบสำคัญ":
            value = requestForm?.form["เลขที่ใบสำคัญ"];
            break;
          case "หน่วยงาน":
            value = employeeData?.DepartmentNameTh;
            break;
          case "รหัสหน่วยงาน":
            value = employeeData?.DepartmentCode;
            break;
          case "ประเภทรายการ":
            value = row?.data?.หมวดวัสดุ;
            break;
          default:
            value =
              (row?.data || {})[label] ?? item.data?.value ?? defaultValue;
            break;
        }

        return {
          [label]: value,
        };
      });
    const objectForm = flatForm.reduce((acc, cur) => ({ ...acc, ...cur }), {});
    formRef.current.flatForm = flatForm;
    formRef.current.objectForm = objectForm;
    form.reset({key: row?.data?.key, ...objectForm});
    setFormState((prv) => ({
      ...prv,
      subject: formRef.current.subject,
      advanceForm: formRef.current.advanceForm,
    }));
  }

  function handleOnHide() {
    setVisible(false);
  }

  function handleDismiss() {
    form.handleSubmit(onSubmit)();
  }

  function setOptions(
    advanceForm: AdvanceForm,
    name: string,
    options: any[],
    label: string = "item",
    value?: string
  ) {
    if (value == null) {
      value = label;
    }
    advanceForm.items = advanceForm.items.map((item) => {
      item.layout = item.layout.map((layout) => {
        switch (layout?.template?.label) {
          case name:
            const items = options.map((option) => ({
              value: value == "$$ROOT" ? option : option[value],
              label: option[label],
            }));
            layout = setDropdownOptions(layout, items);
            break;
        }

        return layout;
      });
      return item;
    });
    return advanceForm;
  }

  function setDropdownOptions(layout: Layout, options: any[]) {
    if (layout?.template?.attribute == null) {
      return layout;
    }
    layout.template.attribute.items = [{ item: "", checked: "Y" }, ...options];
    return layout;
  }

  return (
    <Modal
      visible={visible}
      style={{ borderRadius: "16px", maxWidth: "1300px" }}
      width={"100%"}
      afterClose={handleOnHide}
      closable={false}
      title={
        formState.subject && (
          <span
            style={{ padding: "16px 24px", fontSize: "22px", display: "block" }}
          >
            {formState.subject}
          </span>
        )
      }
      footer={
        <div className={styles.actionButtonContainer}>
          <Button className="p-button-danger" onClick={handleOnHide}>
            ยกเลิก
          </Button>
          &nbsp;
          {formState.advanceForm && (
            <Button onClick={handleDismiss}>บันทึก</Button>
          )}
        </div>
      }
    >
      {formState.advanceForm == null ? (
        <Row justify="center">
          <Skeleton active />
        </Row>
      ) : (
        <WfFormRenderer
          form={form}
          advanceForm={formState.advanceForm}
          onSubmit={onSubmit}
        />
      )}
    </Modal>
  );
}
